import { useEffect, useState } from 'react';

/**
 * useIsPhone
 * A hook that returns true when the visitor appears to be using a phone device.
 * 
 * Detection strategy:
 *   1. Primary: Screen width < 768px (standard mobile breakpoint)
 *   2. Secondary: User agent detection for mobile devices
 *   3. Fallback: Touch capability + small screen size
 *   4. Exclude tablets and iPads explicitly
 */
export function useIsPhone(): boolean {
  const [isPhone, setIsPhone] = useState(false);

  useEffect(() => {
    const checkIsPhone = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Primary detection: Standard mobile breakpoint (768px)
      const isPhoneSize = width < 768;
      
      // User agent checks for mobile devices
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileUA = userAgent.includes('mobile') || 
                        userAgent.includes('android') && userAgent.includes('mobile') ||
                        userAgent.includes('iphone') ||
                        userAgent.includes('ipod') ||
                        userAgent.includes('blackberry') ||
                        userAgent.includes('windows phone');
      
      // Explicitly exclude tablets and iPads
      const isTabletUA = userAgent.includes('tablet') || 
                        userAgent.includes('ipad') || 
                        (userAgent.includes('android') && !userAgent.includes('mobile')) ||
                        userAgent.includes('playbook');
      
      // Touch capability check
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Phone aspect ratio (portrait orientation typical for phones)
      const isPhoneRatio = height > width || width / height <= 1.2;
      
      // Detection logic: prioritize screen size, then user agent, then fallback
      const isPhoneDevice = isPhoneSize || 
                           (isMobileUA && !isTabletUA) || 
                           (hasTouch && isPhoneSize && isPhoneRatio);
      
      // Debug logging in production
      if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
        console.log('Mobile Detection Debug:', {
          width,
          height,
          isPhoneSize,
          isMobileUA,
          isTabletUA,
          hasTouch,
          isPhoneRatio,
          isPhoneDevice,
          userAgent: navigator.userAgent
        });
      }
      
      setIsPhone(isPhoneDevice);
    };

    // Check immediately
    checkIsPhone();
    
    // Add resize listener
    window.addEventListener('resize', checkIsPhone);
    
    // Add orientation change listener for mobile devices
    window.addEventListener('orientationchange', checkIsPhone);
    
    return () => {
      window.removeEventListener('resize', checkIsPhone);
      window.removeEventListener('orientationchange', checkIsPhone);
    };
  }, []);

  return isPhone;
}